import { TrainingKnowledgeFormMembershipsQuery } from 'generated/resources/graphql'

import { TRAINING_MEMBERSHIP_STATUS } from 'modules/trainings/hooks/useTrainingsDatatable'
import {
  CompletionConfiguration,
  TrainingSessions,
  TrainingsTraining,
  TrainingsTrainingClass,
} from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

type FormsMembership =
  TrainingKnowledgeFormMembershipsQuery['forms']['membershipsConnection']['nodes'][number]

export class TrainingParticipantData {
  training: TrainingsTraining
  trainingClasses: TrainingsTrainingClass[]
  formsMemberships?: FormsMembership[] | null
  completionConfiguration: CompletionConfiguration
  private _accessIdToLatestClassMap: Map<number, TrainingsTrainingClass>

  constructor(
    training: TrainingsTraining,
    trainingClasses: TrainingsTrainingClass[],
    completionConfiguration: CompletionConfiguration,
    formsMemberships?: FormsMembership[] | null
  ) {
    this.training = training
    this.trainingClasses = trainingClasses
    this.formsMemberships = formsMemberships
    this.completionConfiguration = completionConfiguration
    this._accessIdToLatestClassMap = this.buildAccessIdToLatestClassMap()
  }

  get totalModulesCount() {
    return this.training?.contentModules?.length ?? 0
  }

  get accessIdToLatestClassMap(): Map<number, TrainingsTrainingClass> {
    return this._accessIdToLatestClassMap
  }

  private buildAccessIdToLatestClassMap(): Map<number, TrainingsTrainingClass> {
    const map = new Map<number, TrainingsTrainingClass>()

    for (const trainingClass of this.trainingClasses) {
      const accessIds = this.collectAccessIds(trainingClass)

      for (const accessId of accessIds) {
        const existing = map.get(accessId)

        if (!existing || this.isTrainingClassMoreRecent(trainingClass, existing)) {
          map.set(accessId, trainingClass)
        }
      }
    }

    return map
  }

  private collectAccessIds(trainingClass: TrainingsTrainingClass): Set<number> {
    const accessIds = new Set<number>()

    for (const member of trainingClass.classMemberships ?? []) {
      const id = member?.access?.id
      if (id) accessIds.add(id)
    }

    return accessIds
  }

  private compareDates(
    current: string | null | undefined,
    existing: string | null | undefined
  ): number {
    if (!current && !existing) return 0
    if (current && !existing) return 1
    if (!current && existing) return -1

    if (!current || !existing) return 0
    return new Date(current).getTime() - new Date(existing).getTime()
  }

  private isTrainingClassMoreRecent(
    current: TrainingsTrainingClass,
    existing: TrainingsTrainingClass
  ): boolean {
    const endDateComparison = this.compareDates(current.endDate, existing.endDate)
    if (endDateComparison !== 0) return endDateComparison > 0

    const startDateComparison = this.compareDates(current.startDate, existing.startDate)
    if (startDateComparison !== 0) return startDateComparison > 0

    const createdAtComparison = this.compareDates(current.createdAt, existing.createdAt)
    return createdAtComparison > 0
  }

  calculateAttendancePercentage(accessId: number): {
    attended: number
    total: number
    percentage: number
  } {
    const trainingClass = this.accessIdToLatestClassMap.get(accessId)
    const sessions = trainingClass?.sessions

    if (!sessions || sessions.length === 0) {
      return { attended: 0, total: 0, percentage: 0 }
    }

    const sessionsForParticipant = this.getSessionsForParticipant(sessions, accessId)
    const totalSessions = sessionsForParticipant.length

    if (totalSessions === 0) {
      return { attended: 0, total: 0, percentage: 0 }
    }

    const attendedSessions = this.getAttendedSessionsCount(sessionsForParticipant, accessId)

    return {
      attended: attendedSessions,
      total: totalSessions,
      percentage: Number(((attendedSessions / totalSessions) * 100).toFixed(1)),
    }
  }

  private getSessionsForParticipant(
    sessions: TrainingSessions,
    accessId: number
  ): TrainingSessions {
    return sessions.filter((session) =>
      session.sessionAttendances?.some((attendance) => attendance.accessId === accessId)
    )
  }

  private getAttendedSessionsCount(sessions: TrainingSessions, accessId: number): number {
    return sessions.filter((session) =>
      session.sessionAttendances?.some(
        (attendance) =>
          attendance.accessId === accessId &&
          attendance.status === TRAINING_MEMBERSHIP_STATUS.COMPLETED
      )
    ).length
  }

  getLatestClassForParticipant(accessId: number): TrainingsTrainingClass | undefined {
    return this.accessIdToLatestClassMap.get(accessId)
  }

  getKnowledgeTestScores(accessId: number): number[] {
    return (this.formsMemberships ?? [])
      .filter((m) => m.respondent?.id === accessId)
      .filter((m) => m.score !== null && m.score !== undefined)
      .map((m) => Number(m.score))
  }

  getCompletionRequirements() {
    const completionConfig = this.completionConfiguration
    const isSessionsRequired = !!(
      completionConfig?.attendancePercentage ||
      (!completionConfig?.attendancePercentage &&
        !completionConfig?.moduleCompletedPercentage &&
        !completionConfig?.knowledgeTestRequired)
    )

    const isModulesRequired = !!completionConfig?.moduleCompletedPercentage
    const knowledgeTestRequired = !!completionConfig?.knowledgeTestRequired

    return { isSessionsRequired, isModulesRequired, knowledgeTestRequired }
  }

  getCompletedModulesCount(employeeId: number | undefined): number {
    if (!employeeId) return 0
    const modules = this.training?.contentModules ?? []

    if (!modules || modules.length === 0) {
      return 0
    }

    const completedModules = modules.filter((module) => {
      const blocks = module?.contentBlocks ?? []

      if (blocks.length === 0) return false

      return blocks.every((block) => {
        const progress = block?.progress
        if (!progress || progress.length === 0) return false

        return progress.some((p) => p?.employeeId === employeeId)
      })
    }).length

    return completedModules
  }
}
